/* eslint-disable @typescript-eslint/no-explicit-any */
import './office-list.scss';
import { Box, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  ContactType,
  DeleteBrokerageOffice,
  getPaginatedNodes,
} from '@converge/graphql';
import {
  IOffices,
  IPerson,
  primaryBrokerageAddress,
} from './accordion.form-container';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { AddOfficeModal } from './office-modal';
import { ApolloError } from '@apollo/client';
import { DeleteLicenseModal } from '../entity/broker-agencies/broker-form/delete-license-modal';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { LicenseModal } from './message-license-modal';
import { BrokersByOfficeDialog } from './brokers-by-office-dialog';
import { config } from '@converge/config';
import { useConvergeSnackBar } from '../../contexts';
import { useState, useMemo, useRef } from 'react';

export interface IOfficeListProps {
  handleSubmitFromChild: () => void;

  brokerageUuid: string | undefined;
  offices?: IOffices[];
  primaryBrokerageAddress: primaryBrokerageAddress;
  brokers: IPerson[];
}

export interface ISelectedDeleteOffice {
  officeUuid: string | null | undefined;
  brokerageUuid: string | null | undefined;
}

const OfficeList = ({
  handleSubmitFromChild,
  brokerageUuid,
  offices,
  brokers,
  primaryBrokerageAddress,
}: IOfficeListProps) => {
  const renderOfficeNameCell = (
    value: string,
    officeId: string,
    rowIndex?: number
  ) => {
    // If office name is too long, truncate it and add ellipsis
    const maxLength = 30;
    const displayValue =
      value && value.length > maxLength
        ? `${value.substring(0, maxLength)}...`
        : value;

    const isEmpty = !value || value === '—' || value === '-';

    const handleOfficeClick = () => {
      if (!isEmpty) {
        setSelectedOfficeForDialog({
          officeId,
          officeName: value || '—',
        });
        setOfficeDialogOpen(true);
      }
    };

    return (
      <Typography
        className="table-cell"
        data-testid={
          rowIndex !== undefined ? `officeName_${rowIndex}` : undefined
        }
        onClick={handleOfficeClick}
        sx={{
          width: '100%',
          paddingLeft: '0px',
          textAlign: 'left',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: isEmpty ? 'default' : 'pointer',
          color: 'inherit',
          textDecoration: isEmpty ? 'none' : 'underline',
          fontWeight: 500,
        }}
      >
        {displayValue || '—'}
      </Typography>
    );
  };
  const renderTableCell = (value: string, testId?: string) => (
    <Typography
      className="table-cell"
      data-testid={testId}
      sx={{
        width: '100%',
        paddingLeft: '0px',

        textAlign: 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value || '—'}
    </Typography>
  );

  const [isBrokerageSave, setIsBrokerageSave] = useState(false);
  const [addNewOffice, setAddNewOffice] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState<
    IOffices | null | undefined
  >(null);

  const [selectedDeleteOffice, setSelectedDeleteOffice] = useState<
    ISelectedDeleteOffice | null | undefined
  >(null);

  // Office brokers dialog state
  const [officeDialogOpen, setOfficeDialogOpen] = useState(false);
  const [selectedOfficeForDialog, setSelectedOfficeForDialog] = useState<{
    officeId: string;
    officeName: string;
  } | null>(null);

  const { notify } = useConvergeSnackBar();
  const { loading, DeleteOfficeFromEntity } = DeleteBrokerageOffice();

  // Use a ref to track the last offices value and prevent unnecessary recalculations
  const lastOfficesRef = useRef<typeof offices>();
  const lastCalculatedListRef = useRef<any[]>([]);

  const brokerageOfficeList = useMemo((): any[] => {
    // If offices is the same reference as last time, return cached result
    if (
      lastOfficesRef.current === offices &&
      lastCalculatedListRef.current.length > 0
    ) {
      return lastCalculatedListRef.current;
    }

    if (!offices || offices.length === 0) {
      lastOfficesRef.current = offices;
      lastCalculatedListRef.current = [];
      return [];
    }

    const result = offices.map((item) => {
      const address = item.node.address;
      const uwTerritories = item?.node.uwTerritories;
      const bdmTerritories = item?.node?.bdmTerritories;

      const officeContact = getPaginatedNodes(item?.node?.persons).filter(
        (contactInfo) => contactInfo.primaryContact
      );
      const primaryContactEmail = officeContact[0]?.contacts?.edges.filter(
        (contact) => contact.node.type === ContactType.Email
      )[0]?.node?.value;

      const brokerName = `${officeContact[0]?.firstName ?? '-'} ${
        officeContact[0]?.lastName ?? ''
      }`;
      return {
        id: item?.node.uuid,
        officeName: item?.node.name ?? '—',
        uwRegions: uwTerritories ? uwTerritories.edges[0]?.node?.name : '—',
        bdmRegions: bdmTerritories ? bdmTerritories.edges[0]?.node?.name : '-',
        bdmRegionsUuid: bdmTerritories
          ? bdmTerritories.edges[0]?.node?.uuid
          : '-',
        address: item?.node?.address
          ? `${address?.street1}, ${
              address?.street2 ? `${address?.street2}, ` : ''
            } ${address?.city}, ${address?.regions?.edges[0]?.node?.name}, ${
              address?.postalCode
            }`
          : '—',
        broker: brokerName ? brokerName : '-',
        primaryContactEmail: primaryContactEmail ? primaryContactEmail : '-',
      };
    });

    // Cache the result and office reference
    lastOfficesRef.current = offices;
    lastCalculatedListRef.current = result;
    return result;
  }, [offices]);

  const noData = [
    {
      id: 0,
      officeName: '—',
      uwRegions: '—',
      address: '—',
      primaryContactEmail: '-',
      broker: '-',
    },
  ];

  const handleDelete = async () => {
    let LicenseResponseErrors;
    try {
      const { errors } = await DeleteOfficeFromEntity({
        variables: {
          entity_uuid: selectedDeleteOffice?.brokerageUuid,
          office_uuid: selectedDeleteOffice?.officeUuid,
        },
      });
      LicenseResponseErrors = errors;

      notify(`Brokerage office has been deleted`, {
        variant: 'success',
      });
      setDeleteModal(false);
    } catch (cause) {
      console.error(
        `Failed to delete brokerage office. Please try again. (${cause})`
      );
      if (cause instanceof ApolloError) {
        LicenseResponseErrors = cause.graphQLErrors;
      }
    }
    const errorMessage = LicenseResponseErrors?.[0]?.message;
    if (errorMessage) {
      notify(
        `Failed to delete brokerage office. Please try again. (${
          errorMessage || 'No data returned by the API'
        })`,
        {
          variant: 'error',
        }
      );
      return;
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'officeName',
      headerName: 'OFFICE NAME',
      flex: 1,
      sortable: true,
      renderCell: (params) =>
        renderOfficeNameCell(
          params.value,
          params.id.toString(),
          params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
        ),
    },
    {
      field: 'uwRegions',
      headerName: 'UW REGION',
      flex: 0.5,
      sortable: true,
      renderCell: (params) =>
        renderTableCell(
          params.value,
          `uwRegions_${
            params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
          }`
        ),
    },
    {
      field: 'address',
      headerName: 'ADDRESS',
      flex: 1.7,
      sortable: true,
      renderCell: (params) =>
        renderTableCell(
          params.value,
          `address_${
            params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
          }`
        ),
    },
    {
      field: 'broker',
      headerName: 'PRIMARY CONTACT',
      flex: 0.8,
      sortable: true,
      renderCell: (params) =>
        renderTableCell(
          params.value,
          `broker_${params.api.getRowIndexRelativeToVisibleRows(params.id) + 1}`
        ),
    },
    {
      field: 'primaryContactEmail',
      headerName: 'PRIMARY CONTACT EMAIL',
      flex: 0.9,
      sortable: true,
      renderCell: (params) =>
        renderTableCell(
          params.value,
          `primaryContactEmail_${
            params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
          }`
        ),
    },
    {
      field: 'iconColumn',
      headerName: '',
      flex: 0.7,
      sortable: false,
      renderCell: (params) => (
        <>
          <Typography
            className="table-cell icons"
            aria-label="info"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1px',
              width: '100%',
              paddingLeft: '20px',
              paddingRight: '20px',
            }}
          >
            {brokerageOfficeList?.length ? (
              <>
                <img
                  src={config.editIcon}
                  className="edit-icon"
                  alt="edit-icon"
                  style={{ cursor: 'pointer' }}
                  data-testid={`edit-office-button_${
                    params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
                  }`}
                  onClick={() => {
                    const selected = offices?.find(
                      (item) => item.node.uuid === params.id
                    );
                    setSelectedOffice(selected || null);
                    setAddNewOffice(true);
                  }}
                />

                <HighlightOffIcon
                  sx={{ cursor: 'pointer', marginTop: '8px' }}
                  data-testid={`delete-office-button_${
                    params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
                  }`}
                  onClick={() => {
                    setDeleteModal(true);
                    setSelectedDeleteOffice({
                      officeUuid: params.id.toString(),
                      brokerageUuid: brokerageUuid,
                    });
                  }}
                />
              </>
            ) : (
              ''
            )}
          </Typography>
        </>
      ),
    },
  ];
  return (
    <>
      <Box
        style={{
          height:
            brokerageOfficeList && brokerageOfficeList?.length > 10
              ? '500px'
              : '',
        }}
      >
        <DataGrid
          className="office-list"
          rowHeight={38}
          disableVirtualization={true}
          sx={{
            border: 'none',
            overscrollBehavior: 'none',
            '& .MuiDataGrid-main': {
              borderRadius: '3px',
              border: '1px solid #cccccc',
              width: '89%',
              paddingBottom: '10px',
              height: 'auto',
              display: 'block',
              alignItems: 'center' /* Center vertically */,
              justifyContent: 'center',
              margin: 'auto',
              overflowX: 'hidden',
              overflowY: 'auto',
            },

            '& .MuiDataGrid-menuIcon': {
              display: 'none', // Hide the menu icon
            },
            '& .MuiDataGrid-root': {
              borderColor: 'transparent',
              border: 'none', // Remove the outer border around the DataGrid
            },
            '& .MuiDataGrid-root  .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-cell.Mui-selected': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'none !important',
              border: 'none', // Remove the outer border around the DataGrid
            },
            '& .MuiDataGrid-cell:hover': {
              backgroundColor: 'none !important',
              border: 'none', // Remove the outer border around the DataGrid
            },
            '& .Mui-selected': {
              backgroundColor: 'inherit', // Removes the selected background color for cells
            },
            '& .MuiDataGrid-row': {
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
              backgroundColor: '#fff !important',
              borderRadius: '3px',
              fontSize: '14px',
              fontWeight: 500,
              marginTop: '0p',
              textAlign: 'center',
              marginBottom: '2px !important',
              '&:hover': {
                borderBottom: '1px solid rgba(224, 224, 224, 1) !important',
              },
            },
            '& .MuiDataGrid-cell[data-field="officeName"]': {
              display: 'flex',
              borderTop: 'none',
              fontWeight: '500',
              padding: '10px 0',
              alignItems: 'center',
              justifyContent: 'left',
              '& .MuiTypography-root': {
                width: '100%',
                padding: '0 20px',
                textWrap: 'initial',
                overflow: 'auto',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            },
            '& .MuiDataGrid-cell:not([data-field="officeName"])': {
              display: 'flex',
              borderTop: 'none',
              fontWeight: '500',
              padding: '10px 0',
              alignItems: 'center',
              justifyContent: 'left',
              '& .MuiTypography-root': {
                width: '100%',
                padding: '0 20px',
                textWrap: 'initial',
                overflow: 'auto',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
              },
            },
            '& .MuiDataGrid-container--top [role="row"], & .MuiDataGrid-container--bottom [role="row"]':
              {
                backgroundColor: '#cefcf6',
                fontSize: '14px',
                fontWeight: 800,
                marginBottom: '0px',
              },

            '& .MuiDataGrid-columnHeader': {
              border: 'none !important',
              height: '32px !important',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              cursor: 'default',
              outline: 'none !important',
              userSelect: 'none',
              padding: '10px 0',
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                width: '100%',
                paddingLeft: '20px',
                paddingRight: '20px',
              },
              '&:focus, &:focus-within': {
                outline: 'none !important',
              },
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
            },
            '& .MuiDataGrid-sortIcon': {
              transform: 'translateY(3px)',
            },

            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: '600', // Make header text bold
              fontSize: '12px',
              lineHeight: '15.88px',
              letterSpacing: '0.5px',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflowX: 'hidden',
              overflowY: 'auto',
              margin: '0px',
            },
            '& .MuiDataGrid-columnSeparator--sideRight': {
              display: 'none', // Hide the right-side border separator between header cells
            },
            '& .MuiDataGrid-footerContainer': {
              display: 'none',
            },
            '& .MuiDataGrid-selectedRowCount': {
              display: 'none', // Hide the "1 row selected" text
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none', // Remove the default outline on focus
              background: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none', // Remove the default outline when focus is within the cell
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              width: '100%',
              paddingBottom: '10px',
            },
          }}
          rows={brokerageOfficeList?.length ? brokerageOfficeList : noData}
          columns={columns}
        />
      </Box>
      <Stack
        className="add-License-container"
        data-testid="add-office-button"
        onClick={() => {
          if (brokerageUuid !== undefined) {
            setAddNewOffice(true);
          } else {
            setIsBrokerageSave(true);
          }
        }}
      >
        <AddCircleOutlineIcon className="add-circle-style" /> ADD OFFICE
      </Stack>
      {isBrokerageSave && (
        <Stack>
          <LicenseModal
            title={'Save Before Adding Offices'}
            onClose={() => {
              setIsBrokerageSave(false);
            }}
            licenseSubmitHandler={() => {
              handleSubmitFromChild();
              setIsBrokerageSave(false);
            }}
            submitLabel="Save Brokerage"
            context={
              'Please save your changes before adding offices to this brokerage.'
            }
          />
        </Stack>
      )}
      {addNewOffice && (
        <Stack>
          <AddOfficeModal
            title={`${selectedOffice ? 'Update Office' : 'Add Office'}`}
            selectedOffice={selectedOffice}
            primaryBrokerageAddress={primaryBrokerageAddress}
            brokers={brokers}
            onClose={() => {
              setAddNewOffice(false);
              setSelectedOffice(null);
            }}
          />
        </Stack>
      )}
      {deleteModal && (
        <Stack>
          <DeleteLicenseModal
            DeleteHandler={() => {
              handleDelete();
            }}
            title={'Delete office Information'}
            onClose={() => {
              setDeleteModal(false);
            }}
            submitLabel="DELETE OFFICE"
            context={'Are you sure you want to delete this office information?'}
            loading={loading}
          />
        </Stack>
      )}

      {/* Office brokers dialog */}
      <BrokersByOfficeDialog
        open={officeDialogOpen}
        onClose={() => {
          setOfficeDialogOpen(false);
          setSelectedOfficeForDialog(null);
        }}
        officeName={selectedOfficeForDialog?.officeName || ''}
        officeId={selectedOfficeForDialog?.officeId || ''}
        entityId={brokerageUuid}
      />
    </>
  );
};

export default OfficeList;
